import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Home } from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

interface CTASectionProps {
  data: LivingInAreaData['nextStep'];
  slug: string;
}

export const CTASection: React.FC<CTASectionProps> = ({ data, slug }) => {
  const message = data?.message?.trim() || 'Find homes available now in this area.';
  const ctaLabel = data?.cta?.trim() || 'Browse Homes';

  return (
    // Changed from slate-950 to white to match your page background
    // Added a subtle top border to separate sections without being jarring
    <footer className="bg-white px-4 py-24 border-t border-slate-100">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        // Added border-2 and a softer shadow for a "floating card" effect on white
        className="relative mx-auto max-w-4xl overflow-hidden rounded-[3rem] border-2 border-slate-100 bg-white p-10 md:p-16 text-center shadow-[0_32px_64px_-15px_rgba(0,0,0,0.1)]"
      >
        {/* Decorative Background Element */}
        <div className="absolute -left-10 -top-10 opacity-[0.03] text-indigo-600 pointer-events-none">
          <Home size={240} strokeWidth={1} />
        </div>

        {/* Header Icon Section */}
        <div className="relative z-10 flex flex-col items-center">
          <motion.div
            className="mb-8 flex h-20 w-20 items-center justify-center rounded-3xl bg-indigo-50 text-indigo-600 shadow-inner"
            animate={{
              rotate: [0, 5, -5, 0],
              y: [0, -4, 0],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Sparkles size={44} strokeWidth={2.5} />
          </motion.div>

          {/* Main Message - High Contrast Slate 900 */}
          <h2 className="mb-10 text-4xl font-black leading-tight tracking-tight text-slate-900 md:text-6xl">
            {message}
          </h2>

          {/* CTA Button - High Impact Gradient */}
          <motion.a
            href={`/browse?location=${encodeURIComponent(slug)}`}
            whileHover={{
              scale: 1.03,
              boxShadow: '0 20px 40px -10px rgba(79, 70, 229, 0.4)',
            }}
            whileTap={{ scale: 0.97 }}
            // no-underline ensures no text-decoration from global styles
            className="no-underline group relative inline-flex items-center justify-center gap-4 overflow-hidden rounded-2xl bg-indigo-600 px-12 py-6 text-2xl font-black text-white transition-all duration-300"
          >
            {/* Glossy Overlay effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 transition-opacity" />

            <span className="relative z-10">{ctaLabel}</span>
            <ArrowRight
              size={28}
              strokeWidth={3}
              className="relative z-10 transition-transform duration-300 group-hover:translate-x-2"
            />
          </motion.a>
        </div>
      </motion.div>
    </footer>
  );
};
